import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MeritPlan } from '@coin/shared/merit-incentive/util';
import { SeasonProcessState, SeasonTypeEnum } from '@coin/shared/core/util-enums';
import { SeasonPlan } from '@coin/shared/core/util-models';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { SeasonSimulation } from '../../../season-mgmt/models/season-simulation.model';
import { DeletePlan, SeasonState, UpdatePlanTitle } from '../../../season-mgmt/store';

interface DialogData {
  plan: SeasonPlan;
  seasonType?: SeasonTypeEnum;
  withoutDelete: boolean;
  handleUpdateExternally: boolean;
  showDescription: boolean;
  descriptionLabel?: string;
  readonly: boolean;
  headerTranslateKey?: string;
  disableChangeDetection?: boolean;
  actionButtonTranslateKey?: string;
}
@Component({
  selector: 'app-edit-season-plan',
  templateUrl: './edit-season-plan.component.html',
  styleUrls: ['./edit-season-plan.component.scss'],
  standalone: false
})
export class EditSeasonPlanComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<EditSeasonPlanComponent>>(MatDialogRef);
  data = inject<DialogData>(MAT_DIALOG_DATA);
  private destroyRef = inject(DestroyRef);
  private store = inject(Store);

  public plan: SeasonPlan | SeasonSimulation | MeritPlan;

  public get isValid(): boolean {
    return !!this.plan?.title?.trim();
  }

  get isDeleteUpdateDisabled(): boolean {
    return [SeasonProcessState.LetterAndPayroll, SeasonProcessState.BudgetAllocation].includes(
      this.store.selectSnapshot(SeasonState.currentSeason)?.state as SeasonProcessState
    );
  }

  public get hasChanges(): boolean {
    return (
      this.data?.disableChangeDetection ||
      this.plan?.title !== this.data?.plan?.title ||
      this.plan?.displayName !== this.data?.plan?.displayName ||
      this.plan?.description !== this.data.plan?.description
    );
  }

  public get showDisplayName(): boolean {
    return this.data?.seasonType === SeasonTypeEnum.IncentiveV2;
  }

  public static openDialog(dialog: MatDialog, data?: DialogData): Observable<SeasonPlan> {
    return dialog
      .open<EditSeasonPlanComponent, DialogData, SeasonPlan>(EditSeasonPlanComponent, {
        disableClose: true,
        minWidth: '500px',
        data
      })
      .beforeClosed();
  }

  ngOnInit(): void {
    this.plan = structuredClone(this.data.plan);
  }

  public async delete(): Promise<void> {
    if (this.data.handleUpdateExternally) {
      return this.dialogRef.close(-1);
    }

    this.store
      .dispatch(
        new DeletePlan({
          plan: this.data.plan,
          seasonType: this.data.seasonType
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dialogRef.close(true));
    return undefined;
  }

  public update(): void {
    if (this.data.handleUpdateExternally) {
      this.dialogRef.close(this.plan);
      return;
    }

    this.store
      .dispatch(
        new UpdatePlanTitle({
          plan: this.data.plan,
          newTitle: this.plan?.title,
          displayName: this.plan?.displayName,
          seasonType: this.data.seasonType
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dialogRef.close(true));
  }

  public close(): void {
    this.dialogRef.close();
  }
}
